import { gameBoyTitle } from "../emulator/saveStats";
import type { MgbaCore } from "../emulator/types";

// Which walkthrough a running game gets. guides/index.json maps a game code
// to a guide directory: a GBA game's 4-letter code from its header, or a
// Game Boy game's header title.

export interface GuideInfo {
  gameCode: string;
  guidePath: string;
}

let index: Promise<Record<string, string>> | null = null;

function guideIndex(): Promise<Record<string, string>> {
  index ??= fetch("/guides/index.json").then((response) => {
    if (!response.ok) throw new Error("Failed to load guide index");
    return response.json();
  });
  index.catch(() => {
    index = null;
  });
  return index;
}

// A zipped game can't be read for its header, so its file name is matched
// against the Pokémon games that have guides. Order matters: "fire red"
// before "red", "leaf green" before "blue".
const ZIP_NAME_CODES: [RegExp, string][] = [
  [/emerald/, "BPEE"],
  [/ruby/, "AXVE"],
  [/sapphire/, "AXPE"],
  [/fire ?red/, "BPRE"],
  [/leaf ?green/, "BPGE"],
  [/crystal/, "PM_CRYSTAL"],
  [/gold/, "POKEMON_GLDAAUE"],
  [/silver/, "POKEMON_SLVAAXE"],
  [/yellow/, "POKEMON YELLOW"],
  [/red/, "POKEMON RED"],
  [/blue/, "POKEMON BLUE"],
];

/** The game code a ROM's header (or, for a zip, its file name) gives, or null. */
export function gameCodeOf(rom: Uint8Array, fileName = ""): string | null {
  if (/\.zip$/i.test(fileName)) {
    const lower = fileName.toLowerCase();
    const match = ZIP_NAME_CODES.find(([pattern]) => pattern.test(lower));
    if (match) return match[1];
  }
  if (rom.length >= 0xb0) {
    const code = String.fromCharCode(...rom.subarray(0xac, 0xb0)).trim();
    if (/^[A-Z0-9]{4}$/.test(code)) return code;
  }
  if (rom.length >= 0x143) {
    const title = gameBoyTitle(rom).trim();
    if (title) return title;
  }
  return null;
}

/**
 * The cartridge file a loaded game runs from: the ROM itself, or the one mGBA
 * unpacked from its zip into the games directory.
 */
export function romFile(core: MgbaCore, romName: string): string {
  if (!/\.zip$/i.test(romName)) return romName;
  return core.FS.readdir(core.filePaths().gamePath).find((entry) => /\.(gba|gbc|gb)$/i.test(entry)) ?? romName;
}

function romBytes(core: MgbaCore, romName: string): Uint8Array | null {
  const { gamePath } = core.filePaths();
  const file = romFile(core, romName);
  try {
    return core.FS.readFile(`${gamePath}/${file}`);
  } catch {
    return null;
  }
}

/** The game code of a ROM the core has loaded, or null. */
export function romGameCode(core: MgbaCore, romName: string): string | null {
  const rom = romBytes(core, romName);
  return rom?.length ? gameCodeOf(rom, romName) : null;
}

export async function detectGuide(core: MgbaCore, romName: string): Promise<GuideInfo | null> {
  const guides = await guideIndex();
  const gameCode = romGameCode(core, romName);
  return gameCode && gameCode in guides ? { gameCode, guidePath: guides[gameCode] } : null;
}
